import { SENSITIVE_PORTS, fetchWithRetry } from './util.js';

const SETTINGS_KEY = 'threatscope_settings';

function getSettings() {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_KEY)) || {};
  } catch (err) {
    console.warn('[threat-intel] failed to parse settings:', err.message);
    return {};
  }
}

export async function checkAbuseIPDB(ip) {
  const settings = getSettings();
  const apiKey = settings.abuseipdbKey;
  if (!apiKey) return null;

  try {
    const res = await fetchWithRetry(`https://api.abuseipdb.com/api/v2/check?ipAddress=${ip}&maxAgeInDays=90`, {
      headers: {
        'Key': apiKey,
        'Accept': 'application/json',
      },
    });
    const data = await res.json();
    if (data.data) {
      return {
        source: 'AbuseIPDB',
        abuseConfidenceScore: data.data.abuseConfidenceScore,
        totalReports: data.data.totalReports,
        lastReportedAt: data.data.lastReportedAt,
        countryCode: data.data.countryCode,
        isp: data.data.isp,
        domain: data.data.domain,
        usageType: data.data.usageType,
        isWhitelisted: data.data.isWhitelisted,
        categories: data.data.reportCategories || [],
      };
    }
    return null;
  } catch (err) {
    console.warn('[threat-intel] AbuseIPDB check failed for', ip, err.message);
    return null;
  }
}

export async function checkAlienVault(ip) {
  const settings = getSettings();
  const apiKey = settings.alienvaultKey;
  if (!apiKey) return null;

  try {
    const res = await fetchWithRetry(`https://otx.alienvault.com/api/v1/indicators/IPv4/${ip}/general`, {
      headers: {
        'X-OTX-API-Key': apiKey,
        'Accept': 'application/json',
      },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      source: 'AlienVault OTX',
      pulseCount: data.pulse_info?.count || 0,
      pulses: (data.pulse_info?.pulses || []).slice(0, 5).map(p => ({
        name: p.name,
        description: p.description,
        adversary: p.adversary || '',
        tags: p.tags || [],
        created: p.created,
      })),
      reputation: data.reputation,
      countryCode: data.country_code,
      asn: data.asn,
    };
  } catch (err) {
    console.warn('[threat-intel] AlienVault check failed for', ip, err.message);
    return null;
  }
}

export async function checkGreyNoise(ip) {
  const settings = getSettings();
  const apiKey = settings.greynoiseKey;

  const results = {};

  const community = async () => {
    try {
      const res = await fetchWithRetry(`https://api.greynoise.io/v3/community/${ip}`, {
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data && data.ip) {
        return {
          source: 'GreyNoise Community',
          noise: data.noise || false,
          riot: data.riot || false,
          classification: data.classification || 'unknown',
          name: data.name || '',
          lastSeen: data.last_seen || '',
        };
      }
      return null;
    } catch (err) {
      console.warn('[threat-intel] GreyNoise community check failed for', ip, err.message);
      return null;
    }
  };

  const enterprise = async () => {
    if (!apiKey) return null;
    try {
      const res = await fetchWithRetry(`https://api.greynoise.io/v2/noise/context/${ip}`, {
        headers: {
          'key': apiKey,
          'Accept': 'application/json',
        },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return {
        source: 'GreyNoise Enterprise',
        noise: data.noise || false,
        classification: data.classification || 'unknown',
        noiseScore: data.noise_score || 0,
        actor: data.actor || '',
        tags: data.tags || [],
        bot: data.bot || false,
        vpn: data.vpn || false,
        tor: data.tor || false,
        proxy: data.proxy || false,
        lastSeen: data.last_seen || '',
      };
    } catch (err) {
      console.warn('[threat-intel] GreyNoise enterprise check failed for', ip, err.message);
      return null;
    }
  };

  const [communityResult, enterpriseResult] = await Promise.all([community(), enterprise()]);

  if (communityResult) {
    results.community = communityResult;
  }
  if (enterpriseResult) {
    results.enterprise = enterpriseResult;
  }

  return Object.keys(results).length > 0 ? results : null;
}

export async function checkShodan(ip) {
  const settings = getSettings();
  const apiKey = settings.shodanKey;
  if (!apiKey) return null;

  try {
    const res = await fetchWithRetry(`https://api.shodan.io/shodan/host/${ip}?key=${apiKey}`);
    if (res.status === 401 || res.status === 403) return null;
    if (res.status === 404) return null;
    if (!res.ok) return null;

    const data = await res.json();

    return {
      ports: data.ports || [],
      totalPorts: (data.ports || []).length,
      hostnames: data.hostnames || [],
      org: data.org || '',
      isp: data.isp || '',
      os: data.os || '',
      countryName: data.country_name || '',
      city: data.city || '',
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      tags: data.tags || [],
      vulns: data.vulns
        ? Object.entries(data.vulns).map(([cve, info]) => ({
            cve,
            published: info?.pub_time ? new Date(info.pub_time).toISOString().slice(0, 10) : null,
          })).slice(0, 20)
        : [],
      totalVulns: data.vulns ? Object.keys(data.vulns).length : 0,
      hasSensitivePorts: (data.ports || []).some(p => SENSITIVE_PORTS.includes(p)),
      sensitivePortsFound: (data.ports || []).filter(p => SENSITIVE_PORTS.includes(p)),
      lastUpdate: data.last_update || '',
    };
  } catch (err) {
    console.warn('[threat-intel] Shodan check failed for', ip, err.message);
    return null;
  }
}

export async function enrichIP(ip) {
  const [abuseResult, alienResult, greyResult, shodanResult] = await Promise.all([
    checkAbuseIPDB(ip),
    checkAlienVault(ip),
    checkGreyNoise(ip),
    checkShodan(ip),
  ]);

  return {
    abuseIPDB: abuseResult,
    alienVault: alienResult,
    greynoise: greyResult,
    shodan: shodanResult,
  };
}
